/**
 * "Дараагийн алхам": which actions a booking offers right now. Pure so the
 * detail dialog and the front-desk rows agree and Jest can cover every case.
 */
export type NextStepAction =
  | "RECORD_PAYMENT"
  | "CONFIRM_WITHOUT_PAYMENT"
  | "EXTEND_HOLD"
  | "MARK_CAME"
  | "MARK_NO_SHOW"
  | "RESCHEDULE"
  | "SETTLE_REFUND"
  | "BOOK_NEXT"
  | "MARK_CALLED"
  | "RESOLVE_CHANGE_REQUEST";

export interface NextStepsInput {
  status: string;
  startTime: string;
  settings: Record<string, any> | null;
  paymentSummary: {
    price: number | null;
    paid: number;
    balance: number | null;
  };
  now: Date;
}

/** Short booking reference (transfer «гүйлгээний утга»); mirrors the backend. */
export function bookingCode(bookingId: string): string {
  return bookingId.replace(/-/g, "").slice(0, 8).toUpperCase();
}

/** Console booking waiting for offline payment (incl. legacy rows with no deadline). */
export function isOfflineHold(
  status: string,
  settings: Record<string, any> | null,
): boolean {
  return (
    status === "PENDING" &&
    settings?.source === "admin_console" &&
    !settings?.paymentCheckout
  );
}

/** «📞 Залгасан» is offered this long before the visit. */
const CALL_WINDOW_MS = 48 * 3_600_000;

/** Before the visit: answer an open change request, then the reminder call. */
function contactSteps(input: NextStepsInput): NextStepAction[] {
  const s = input.settings ?? {};
  const out: NextStepAction[] = [];
  if (s.changeRequest && !s.changeRequest.resolvedAt) out.push("RESOLVE_CHANGE_REQUEST");
  const untilStart = new Date(input.startTime).getTime() - input.now.getTime();
  if (!s.customerReply && !s.callHandled && untilStart > 0 && untilStart <= CALL_WINDOW_MS) {
    out.push("MARK_CALLED");
  }
  return out;
}

export function nextSteps(input: NextStepsInput): NextStepAction[] {
  const { status, settings, paymentSummary, now } = input;
  const owes = (paymentSummary.balance ?? 0) > 0;
  const priced = (paymentSummary.price ?? 0) > 0;
  const started = now.getTime() >= new Date(input.startTime).getTime();

  switch (status) {
    case "PENDING":
      if (!isOfflineHold(status, settings)) return [];
      return [
        ...(priced ? (["RECORD_PAYMENT"] as const) : []),
        "CONFIRM_WITHOUT_PAYMENT",
        "EXTEND_HOLD",
      ];
    case "CONFIRMED":
      // Check-in: the customer often pays the rest right then.
      if (started) return ["MARK_CAME", ...(owes ? (["RECORD_PAYMENT"] as const) : []), "MARK_NO_SHOW"];
      return [
        "RESCHEDULE",
        ...(owes ? (["RECORD_PAYMENT"] as const) : []),
        ...contactSteps(input),
      ];
    case "COMPLETED":
      return owes ? ["RECORD_PAYMENT", "BOOK_NEXT"] : ["BOOK_NEXT"];
    case "NO_SHOW":
      return ["MARK_CAME", "BOOK_NEXT"];
    case "CANCELLED":
      return settings?.refundOwed && !settings.refundOwed.settledAt
        ? ["SETTLE_REFUND"]
        : [];
    default:
      return [];
  }
}

/**
 * «Цуцлах» availability. Past bookings are view-only, except an offline hold:
 * staff must be able to clear an overdue "wait for payment" booking even
 * after its time passed.
 */
export function canCancelBooking(input: {
  status: string;
  endTime: string;
  settings: Record<string, any> | null;
  now: Date;
}): boolean {
  if (isOfflineHold(input.status, input.settings)) return true;
  if (input.status !== "PENDING" && input.status !== "CONFIRMED") return false;
  return new Date(input.endTime).getTime() >= input.now.getTime();
}
